// REL_1.1.15E F-311 (S-1230) — Cookie-consent banner (US CCPA/CPRA: notice + opt-out model).
// Ships WITH the GTM inject: it only renders where GTM is active (VITE_GTM_ID present = production build).
// The Consent-Mode-v2 DEFAULT is set at BUILD TIME in <head> (vite.config, before GTM loads); this banner
// only issues gtag('consent','update',…) on the visitor's choice and persists it to localStorage + a
// `.deedo.ai`-scoped cookie so the choice carries across the www / mark / app subdomains (analytics spec §3).
import { useState } from "react";

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;
const STORAGE_KEY = "deedo-consent";
const PRIVACY_URL = `${import.meta.env.VITE_APP_BASE_URL || "https://app.deedo.ai"}/privacy-policy`;
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

type Choice = "granted" | "denied";

function readChoice(): Choice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

function persist(choice: Choice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* localStorage unavailable (private mode / quota) — ignore */
  }
  // `.deedo.ai`-scoped so the choice carries across subdomains (www/mark/app). Prod is HTTPS → Secure.
  try {
    document.cookie = `${STORAGE_KEY}=${choice}; domain=.deedo.ai; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`;
  } catch {
    /* cookie write failed — ignore */
  }
}

function updateConsent(choice: Choice): void {
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      ad_storage: choice,
      analytics_storage: choice,
      ad_user_data: choice,
      ad_personalization: choice,
    });
  }
}

export default function ConsentBanner() {
  const [choice, setChoice] = useState<Choice | null>(() => readChoice());

  // Only where GTM is active (production), and only until the visitor has made a choice.
  if (!GTM_ID || choice !== null) return null;

  const decide = (c: Choice) => {
    persist(c);
    updateConsent(c);
    setChoice(c);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/95 px-4 py-4 shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          We use cookies for analytics to understand how visitors use Deedo and improve the site. You can
          opt out anytime.{" "}
          {/* brand-primary for AA link contrast on white (brand-secondary cyan fails; F-275 lesson) */}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[var(--brand-primary)] underline hover:no-underline"
          >
            Privacy Policy
          </a>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="rounded-lg bg-[var(--brand-primary)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
